import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { config, debugLog } from '../utils/config';

interface RevenueMetrics {
  totalReceived: string;
  currentlyOnHold: string;
  currentThreshold: string;
  thresholdPercentage: number;
  pendingConversions: number;
  processingStatus: 'active' | 'paused' | 'error';
}

interface RevenueTransaction {
  id: string;
  timestamp: string;
  type: 'incoming_usd' | 'outgoing_conversion';
  amount: string;
  source: string;
  status: 'completed' | 'pending' | 'failed';
  description?: string;
  conversionDetails?: {
    exchangeRate?: number;
    ethAmount?: string;
    fees?: string;
  };
}

export const CompanyRevenue: React.FC = () => {
  const [metrics, setMetrics] = useState<RevenueMetrics>({
    totalReceived: '0',
    currentlyOnHold: '0',
    currentThreshold: '1,000',
    thresholdPercentage: 0,
    pendingConversions: 0,
    processingStatus: 'active'
  });

  const [incomingTransactions, setIncomingTransactions] = useState<RevenueTransaction[]>([]);
  const [outgoingTransactions, setOutgoingTransactions] = useState<RevenueTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRevenueData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Test LocalStack connection if enabled
      if (config.localstack.enabled) {
        const connectionTest = await api.testLocalStack();
        if (!connectionTest.connected) {
          debugLog('LocalStack connection failed:', connectionTest.error);
          setError(`LocalStack connection failed: ${connectionTest.error}`);
          return;
        }
      }

      // Load revenue data in parallel
      const [revenueMetrics, revenueTransactions] = await Promise.all([
        api.revenue.getMetrics(),
        api.revenue.getTransactions(50)
      ]);

      setMetrics(revenueMetrics);
      
      // Separate incoming and outgoing transactions
      const incoming = revenueTransactions.filter(tx => tx.type === 'incoming_usd');
      const outgoing = revenueTransactions.filter(tx => tx.type === 'outgoing_conversion');
      
      setIncomingTransactions(incoming);
      setOutgoingTransactions(outgoing);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load revenue data';
      debugLog('Revenue data loading error:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRevenueData();
    
    // Set up periodic refresh
    const interval = setInterval(loadRevenueData, config.intervals.medium);
    
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="placeholder-glow mb-4">
          <div className="placeholder col-4 mb-2" style={{ height: '2rem' }}></div>
          <div className="placeholder col-6" style={{ height: '1rem' }}></div>
        </div>
        <div className="row g-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="col-12 col-md-6 col-lg-3">
              <div className="card placeholder-glow">
                <div className="card-body">
                  <div className="placeholder col-12" style={{ height: '120px' }}></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="mb-4">
        <h1 className="h2 mb-2">Company Revenue Dashboard</h1>
        <p className="text-muted">
          Track revenue collection and allocation for BWS token purchases
          {config.localstack.enabled && (
            <span className="ms-2 badge bg-primary-subtle text-primary border border-primary">
              <i className="bi bi-hdd-network me-1"></i>
              LocalStack Mode
            </span>
          )}
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger d-flex align-items-start mb-4" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-3 fs-4"></i>
          <div className="flex-grow-1">
            <h5 className="alert-heading mb-1">Connection Error</h5>
            <p className="mb-2">{error}</p>
            <button
              onClick={loadRevenueData}
              className="btn btn-sm btn-outline-danger"
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Revenue Metrics Overview */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-6 col-lg-3">
          <div className="card shadow-sm h-100 border-success">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <p className="text-muted mb-1 small">Total Received</p>
                  <h3 className="mb-0">{formatCurrency(metrics.totalReceived)}</h3>
                </div>
                <div className="p-2 rounded-circle bg-success-subtle">
                  <i className="bi bi-currency-dollar fs-4 text-success"></i>
                </div>
              </div>
              <small className="text-muted">All time revenue</small>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-3">
          <div className="card shadow-sm h-100 border-warning">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <p className="text-muted mb-1 small">Currently On Hold</p>
                  <h3 className="mb-0">{formatCurrency(metrics.currentlyOnHold)}</h3>
                </div>
                <div className="p-2 rounded-circle bg-warning-subtle">
                  <i className="bi bi-clock fs-4 text-warning"></i>
                </div>
              </div>
              <small className="text-muted">{metrics.thresholdPercentage.toFixed(1)}% of threshold</small>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-3">
          <div className="card shadow-sm h-100 border-primary">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <p className="text-muted mb-1 small">Current Threshold</p>
                  <h3 className="mb-0">{formatCurrency(metrics.currentThreshold)}</h3>
                </div>
                <div className="p-2 rounded-circle bg-primary-subtle">
                  <i className="bi bi-graph-up-arrow fs-4 text-primary"></i>
                </div>
              </div>
              <small className="text-muted">Batch conversion trigger</small>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-3">
          <div className={`card shadow-sm h-100 ${
            metrics.processingStatus === 'active'
              ? 'border-success'
              : metrics.processingStatus === 'paused'
              ? 'border-warning'
              : 'border-danger'
          }`}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <p className="text-muted mb-1 small">Processing Status</p>
                  <h3 className={`mb-0 text-capitalize ${
                    metrics.processingStatus === 'active' ? 'text-success' :
                    metrics.processingStatus === 'paused' ? 'text-warning' : 'text-danger'
                  }`}>{metrics.processingStatus}</h3>
                </div>
                <div className={`p-2 rounded-circle ${
                  metrics.processingStatus === 'active' ? 'bg-success-subtle' :
                  metrics.processingStatus === 'paused' ? 'bg-warning-subtle' : 'bg-danger-subtle'
                }`}>
                  <i className={`bi bi-activity fs-4 ${
                    metrics.processingStatus === 'active' ? 'text-success' :
                    metrics.processingStatus === 'paused' ? 'text-warning' : 'text-danger'
                  }`}></i>
                </div>
              </div>
              <small className="text-muted">{metrics.pendingConversions} pending</small>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Logs */}
      <div className="row g-3 mb-4">
        {/* Incoming USD Log */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-transparent">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-arrow-down-right-circle fs-5 text-success"></i>
                <h2 className="h5 fw-semibold mb-0">Incoming USD Revenue</h2>
              </div>
              <p className="small text-muted mb-0">Company revenue deposits</p>
            </div>
            <div className="card-body">
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {incomingTransactions.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-file-earmark-text fs-1 d-block mb-2 text-muted"></i>
                    <p className="mb-0">No incoming transactions yet</p>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {incomingTransactions.map((tx) => (
                      <div key={tx.id} className="border-start border-success border-4 ps-3 py-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <p className="fw-medium text-success mb-1">
                              {formatCurrency(tx.amount)}
                            </p>
                            <p className="small text-muted mb-1">{tx.source}</p>
                            <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                              {formatTimestamp(tx.timestamp)}
                            </p>
                          </div>
                          <div>
                            <span className={`badge ${
                              tx.status === 'completed'
                                ? 'bg-success'
                                : tx.status === 'pending'
                                ? 'bg-warning'
                                : 'bg-danger'
                            }`}>
                              {tx.status}
                            </span>
                          </div>
                        </div>
                        {tx.description && (
                          <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.75rem' }}>{tx.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Outgoing Conversion Log */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-transparent">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-arrow-up-right-circle fs-5 text-primary"></i>
                <h2 className="h5 fw-semibold mb-0">USD→ETH Conversions</h2>
              </div>
              <p className="small text-muted mb-0">Coinbase Pro conversion requests</p>
            </div>
            <div className="card-body">
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {outgoingTransactions.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-file-earmark-text fs-1 d-block mb-2 text-muted"></i>
                    <p className="mb-0">No conversions yet</p>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {outgoingTransactions.map((tx) => (
                      <div key={tx.id} className="border-start border-primary border-4 ps-3 py-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <p className="fw-medium text-primary mb-1">
                              {formatCurrency(tx.amount)}
                            </p>
                            <p className="small text-muted mb-1">{tx.source}</p>
                            <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                              {formatTimestamp(tx.timestamp)}
                            </p>
                          </div>
                          <div>
                            <span className={`badge ${
                              tx.status === 'completed'
                                ? 'bg-success'
                                : tx.status === 'pending'
                                ? 'bg-warning'
                                : 'bg-danger'
                            }`}>
                              {tx.status}
                            </span>
                          </div>
                        </div>
                        {tx.conversionDetails && (
                          <div className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                            {tx.conversionDetails.ethAmount && (
                              <p className="mb-1">→ {tx.conversionDetails.ethAmount} ETH</p>
                            )}
                            {tx.conversionDetails.exchangeRate && (
                              <p className="mb-1">Rate: ${tx.conversionDetails.exchangeRate.toLocaleString()}/ETH</p>
                            )}
                            {tx.conversionDetails.fees && (
                              <p className="mb-0">Fees: {formatCurrency(tx.conversionDetails.fees)}</p>
                            )}
                          </div>
                        )}
                        {tx.description && (
                          <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.75rem' }}>{tx.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Allocation Settings */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-transparent">
          <h2 className="h5 fw-semibold mb-1">Revenue Allocation Configuration</h2>
          <p className="small text-muted mb-0">Current settings for BWS token purchase allocation</p>
        </div>
        <div className="card-body">
          <div className="row g-4">
            <div className="col-12 col-md-4">
              <label className="small fw-medium text-muted d-block mb-2">Allocation Percentage</label>
              <div className="h3 fw-semibold text-primary mb-1">10%</div>
              <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>Of net revenue allocated for BWS purchases</p>
            </div>
            <div className="col-12 col-md-4">
              <label className="small fw-medium text-muted d-block mb-2">Minimum Batch Size</label>
              <div className="h3 fw-semibold text-success mb-1">$1,000</div>
              <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>Minimum amount to trigger conversion</p>
            </div>
            <div className="col-12 col-md-4">
              <label className="small fw-medium text-muted d-block mb-2">Processing Mode</label>
              <div className="h3 fw-semibold text-info mb-1">Batch</div>
              <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>Batching enabled for cost efficiency</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};